/**
 * The partner's commission progress in a contractor's words (Warren, 21 October 2026; sales comp change, 27 October 2026).
 * Standard has one rate on every qualifying charger, so a Standard partner sees qualification progress only ("2 more
 * qualifying chargers until commission switches on"), then the rate. A Golden partner sees the next step on the Golden
 * ladder ("Charger 47 of 50 to reach 4.00%"). A point is one percent of each paid charging session, so rates read as
 * percents. The counts and rates come from the Api's program view (spProgramView); this only words them.
 */
export const SP_QUALIFICATION_COUNT = 4;
export interface TierProgramView {
  program: 'standard' | 'golden'; eligible: boolean; current_rate_bps: number;
  next_tier: { count: number; rate_bps: number } | null; ladder: Array<{ from: number; rate_bps: number }>;
}

/** 250 basis points as "2.5%". */
export const sessionPercent = (bps: number) => `${Number((bps / 100).toFixed(2))}%`;
/** Golden rates read with two decimals, as the Golden ladder is published: 400 basis points as "4.00%". */
export const goldenPercent = (bps: number) => `${(bps / 100).toFixed(2)}%`;
/** A rate in its program's published form: Standard "3%", Golden "3.50%". */
export const programPercent = (bps: number, program: TierProgramView['program']) => program === 'golden' ? goldenPercent(bps) : sessionPercent(bps);
export const ordinal = (n: number) => `${n}${n % 10 === 1 && n % 100 !== 11 ? 'st' : n % 10 === 2 && n % 100 !== 12 ? 'nd' : n % 10 === 3 && n % 100 !== 13 ? 'rd' : 'th'}`;

export function tierProgress(deployed: number, program: TierProgramView): { headline: string; detail: string | null } | null {
  if (!program.eligible) {
    const remaining = Math.max(1, SP_QUALIFICATION_COUNT - deployed);
    const rate = programPercent(program.ladder[0]?.rate_bps ?? program.current_rate_bps, program.program);
    return { headline: `${remaining} more qualifying charger${remaining === 1 ? '' : 's'} until commission switches on`,
      detail: `Commission switches on at your ${ordinal(SP_QUALIFICATION_COUNT)} qualifying charger. Then you earn ${rate} of each paid session on every qualifying charger, your first three included.` };
  }
  if (program.program !== 'golden') return { headline: `You earn ${sessionPercent(program.current_rate_bps)} of each paid session on every qualifying charger`, detail: null };
  if (!program.next_tier) return null;
  const next = program.next_tier;
  return { headline: `Charger ${deployed} of ${next.count} to reach ${goldenPercent(next.rate_bps)}`,
    detail: `Chargers you install from your ${ordinal(next.count)} on earn ${goldenPercent(next.rate_bps)} of each paid session (now ${goldenPercent(program.current_rate_bps)}). A charger keeps the rate it launched with.` };
}

/** The Golden ladder, one line per step: "From your 50th to your 99th charger: 4.00% of each paid session". */
export function goldenLadderLines(ladder: TierProgramView['ladder']): string[] {
  return ladder.map((r, i) => {
    const next = ladder[i + 1];
    const span = r.from === 1 ? `Your first${next ? ` to your ${ordinal(next.from - 1)}` : ''} charger` : next ? `From your ${ordinal(r.from)} to your ${ordinal(next.from - 1)} charger` : `From your ${ordinal(r.from)} charger on`;
    return `${span}: ${goldenPercent(r.rate_bps)} of each paid session`;
  });
}
